import {
  Images,
  Film,
  Presentation,
  Megaphone,
  Boxes,
  Globe,
  FolderOpen,
} from "lucide-react";

/**
 * The delivery scopes a portfolio project is organised by — one per part of the
 * package we sell, so every department has an obvious place to put its work and
 * sales gets one complete view of what was delivered.
 *
 * Mirrors PORTFOLIO_SCOPES in backend/server.py. Keys must match; the backend
 * owns validation, this owns how each scope looks and reads.
 *
 * `layout` decides how the section presents its contents:
 *   gallery   — an image mosaic leads, links follow as chips
 *   video     — links become 16:9 play cards, stills follow
 *   documents — decks and files become tall cover cards
 *   feature   — one wide banner for the single URL that matters
 *   files     — plain hairline link rows
 *
 * `cta` is what the package card's button says — the reader should know what
 * opens before they click it.
 */
export const SCOPES = [
  {
    key: "renders",
    label: "Renders & Stills",
    short: "Renders",
    Icon: Images,
    blurb: "Exteriors, interiors and amenity stills delivered to the client.",
    cta: "View renders",
    noun: "still",
    layout: "gallery",
    acceptsImages: true,
    acceptsDocuments: false,
    linkTypes: ["folder_link", "other_link"],
    emptyHint: "Upload the delivered stills, or link the Dropbox folder they live in.",
  },
  {
    key: "video",
    label: "Video & Animation",
    short: "Video",
    Icon: Film,
    blurb: "Animations, walkthroughs, AI motion and social cutdowns.",
    cta: "View video",
    noun: "film",
    layout: "video",
    acceptsImages: true,
    acceptsDocuments: false,
    linkTypes: ["video_link", "folder_link"],
    emptyHint: "Add the Vimeo or YouTube link for each animation we delivered.",
  },
  {
    key: "collateral",
    label: "Presentations & Collateral",
    short: "Collateral",
    Icon: Presentation,
    blurb: "Decks, brochures, boards and print collateral.",
    cta: "View collateral",
    noun: "piece",
    layout: "documents",
    acceptsImages: true,
    acceptsDocuments: true,
    linkTypes: ["doc_link", "folder_link", "other_link"],
    emptyHint: "Upload the deck as a PDF, or link the presentation.",
  },
  {
    key: "campaign",
    label: "Marketing Campaign",
    short: "Campaign",
    Icon: Megaphone,
    blurb: "Campaign assets — ads, social, email, signage.",
    cta: "View campaign",
    noun: "asset",
    layout: "gallery",
    acceptsImages: true,
    acceptsDocuments: true,
    linkTypes: ["web_link", "video_link", "doc_link", "folder_link"],
    emptyHint: "Upload the campaign assets we produced, or link the live campaign.",
  },
  {
    key: "vexos",
    label: "VEXOS Platform",
    short: "VEXOS",
    Icon: Boxes,
    blurb: "The interactive VEXOS experience built for this development.",
    cta: "Open VEXOS",
    noun: "platform",
    layout: "feature",
    acceptsImages: true,
    acceptsDocuments: false,
    linkTypes: ["vexos_link"],
    emptyHint: "Add the VEXOS platform link.",
  },
  {
    key: "website",
    label: "Website",
    short: "Website",
    Icon: Globe,
    blurb: "The project website we designed and shipped.",
    cta: "Visit website",
    noun: "site",
    layout: "feature",
    acceptsImages: true,
    acceptsDocuments: false,
    linkTypes: ["web_link"],
    emptyHint: "Add the live website URL, and a screenshot of the homepage.",
  },
  {
    key: "files",
    label: "Project Files",
    short: "Files",
    Icon: FolderOpen,
    blurb: "Master delivery folders and anything that doesn't sit in one scope.",
    cta: "Open files",
    noun: "file",
    layout: "files",
    acceptsImages: true,
    acceptsDocuments: true,
    linkTypes: ["folder_link", "other_link"],
    emptyHint: "Link the master delivery folder for this project.",
  },
];

export const SCOPE_BY_KEY = SCOPES.reduce((acc, s) => ({ ...acc, [s.key]: s }), {});

/** Departments own different parts of the scope, so curating the portfolio
 *  isn't an admin-only job. Mirrors PORTFOLIO_CURATOR_ROLES on the backend,
 *  which is what actually enforces it. */
export const PORTFOLIO_CURATOR_ROLES = [
  "COO", "Admin", "Program Manager", "Project Manager",
  "Marketing PM", "Production", "Sales",
];

export const canCuratePortfolio = (user) => PORTFOLIO_CURATOR_ROLES.includes(user?.role);

/** The scopes that make up a sellable package — Project Files is the catch-all
 *  and is never something a client buys, so it stays off the package board. */
export const PACKAGE_SCOPES = SCOPES.filter(s => s.key !== "files");

// Where an item lands when it carries no explicit scope (legacy media written
// before scopes existed). Mirrors _LEGACY_SCOPE_BY_MEDIA_TYPE on the backend.
const LEGACY_SCOPE_BY_MEDIA_TYPE = {
  image: "renders",
  document: "files",
  video_link: "video",
  vexos_link: "vexos",
  web_link: "website",
  doc_link: "collateral",
  folder_link: "files",
  dropbox_link: "files",
  other_link: "files",
};

export function deriveScope(media) {
  if (media?.scope && SCOPE_BY_KEY[media.scope]) return media.scope;
  return LEGACY_SCOPE_BY_MEDIA_TYPE[media?.media_type] || "files";
}

/** Split a project's media into { [scopeKey]: { images, documents, links } }. */
export function groupByScope(media = []) {
  const groups = {};
  SCOPES.forEach(s => {
    groups[s.key] = { images: [], documents: [], links: [], all: [] };
  });

  media.forEach(m => {
    const bucket = groups[deriveScope(m)];
    if (!bucket) return;
    if (m.media_type === "image") bucket.images.push(m);
    else if (m.media_type === "document") bucket.documents.push(m);
    else bucket.links.push(m);
    bucket.all.push(m);
  });

  return groups;
}

/**
 * The still that fronts a scope's package card. Its own first image if it has
 * one, otherwise a video poster — so a Video card looks like the film it holds
 * even when nobody uploaded a frame from it.
 *
 * Returns `{ media }` for something we store (resolve it through mediaUrl) or
 * `{ urls }` for posters hosted elsewhere. Empty when there's nothing to show.
 */
export function scopeCover(group = {}) {
  if (group.images?.length) return { media: group.images[0] };
  const withPoster = (group.links || []).find(
    l => l.media_type === "video_link" && videoPosters(l.url || "").length
  );
  if (withPoster) return { urls: videoPosters(withPoster.url) };
  return {};
}

/** "6 stills · 1 link" — what's actually on file, in words rather than a bare
 *  number, so the card reads without the reader counting. */
export function summarizeScope(scope, group = {}) {
  const images = group.images?.length || 0;
  const documents = group.documents?.length || 0;
  const links = group.links || [];
  const videos = links.filter(l => l.media_type === "video_link").length;
  const others = links.length - videos;

  // A platform or a site is one thing that's either up or it isn't; counting
  // its screenshots would bury the only fact that matters.
  const parts = scope.layout === "feature"
    ? [links.length ? "Live" : null, plural(images, "still")]
    : [
      plural(images, scope.layout === "gallery" ? scope.noun : "still"),
      plural(videos, "film"),
      plural(documents, "document"),
      plural(others, "link"),
    ];

  return parts.filter(Boolean).slice(0, 2).join(" · ");
}

const plural = (n, noun) => (n ? `${n} ${noun}${n === 1 ? "" : "s"}` : null);

/**
 * Poster frames for a video link, best first, for a consumer that falls back
 * down the list as each one fails. Vimeo needs an API call to resolve one, so
 * those get a play card instead.
 *
 * maxresdefault leads because it's the true 16:9 frame — hqdefault is a 4:3
 * canvas with black bars baked into it, and those bars land across a card's
 * title. It only exists for videos uploaded in HD, hence the fallback.
 */
export function videoPosters(url = "") {
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (!yt) return [];
  return [
    `https://img.youtube.com/vi/${yt[1]}/maxresdefault.jpg`,
    `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg`,
  ];
}

/** "dropbox.com/scl/fo/..." → "dropbox.com" — a link's home, for the subtitle. */
export function linkHost(url = "") {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url.replace(/^https?:\/\//, "").split("/")[0];
  }
}
